/**
 * Reviews Slider with Swiper
 *
 * This script initializes and configures the Swiper slider for the reviews section,
 * providing smooth sliding transitions, responsive behavior, and accessibility features.
 *
 * @version 1.1.0
 */

class ReviewsSlider {
	/**
	 * Initialize the Reviews Slider for a specific section root
	 * @param {HTMLElement} root
	 */
	constructor(root) {
		// Section root element
		this.root = root;
		// Initialize properties
		this.swiper = null;
		this.swiperContainer = this.root.querySelector('.reviews-slider__container');

		// Only proceed if the container exists within this section
		if (!this.swiperContainer) return;

		// Cache nav and write-review wrappers within this section
		this.navElement = this.root.querySelector('.reviews-slider__nav');
		this.writeReviewElement = this.root.querySelector('.reviews-slider__write-review');
		this.navPlaceholder = null;

		// Create a placeholder to remember the original nav position (so it doesn't move on desktop)
		this.ensureNavPlaceholder();

		// Initialize components
		this.initSwiper();
		this.setupAccessibility();
		this.setupResizeHandler();
	}

	/**
	 * Initialize and configure the Swiper instance
	 */
	initSwiper() {
		// Create new Swiper instance with optimized configuration
		this.swiper = new Swiper(this.swiperContainer, {
			// Core settings
			slidesPerView: 2,
			spaceBetween: 32,
			speed: 600, // Increased for smoother transitions
			cssMode: false, // Disable CSS mode for smoother animations

			// Improved transition settings
			watchSlidesProgress: true,
			slideToClickedSlide: true,
			normalizeSlideIndex: true,
			centeredSlides: false,
			loop: true,
			// Prevent jumping by using better slide calculation
			roundLengths: true, // Round values to prevent blurry text

			// Custom easing for smoother transitions
			effect: 'slide',
			slidesPerGroup: 1, // Move exactly 1 slide at a time

			// Improved touch handling
			touchRatio: 1,
			touchAngle: 45,
			grabCursor: true,
			simulateTouch: true,

			// Resistance settings for smoother edge behavior
			resistance: true,
			resistanceRatio: 0.85,

			// Prevent jumping at the beginning/end
			threshold: 5, // Minimum distance for a swipe action

			// Navigation (scoped to this section)
			navigation: {
				nextEl: this.navElement ? this.navElement.querySelector('.swiper-button-next') : null,
				prevEl: this.navElement ? this.navElement.querySelector('.swiper-button-prev') : null,
				disabledClass: 'swiper-button-disabled',
			},

			// Scrollbar (mobile)
			scrollbar: {
				el: this.swiperContainer.querySelector('.swiper-scrollbar'),
				draggable: true,
				hide: false,
			},


			// Accessibility
			a11y: {
				enabled: true,
				prevSlideMessage: 'Previous review',
				nextSlideMessage: 'Next review',
				firstSlideMessage: 'This is the first review',
				lastSlideMessage: 'This is the last review',
				paginationBulletMessage: 'Go to review {{index}}'
			},

			// Keyboard control
			keyboard: {
				enabled: true,
				onlyInViewport: true
			},

			// Responsive breakpoints
			breakpoints: {
				// Mobile
				320: {
					slidesPerView: 'auto',
					spaceBetween: 12
				},
				// Tablet
				993: {
					slidesPerView: 1,
					spaceBetween: 12
				},
				// Desktop
				1290: {
					slidesPerView: 2,
					spaceBetween: 16
				},
				1300: {
					slidesPerView: 2,
					spaceBetween: 24
				}
			},

			// Observer settings for better reactivity
			observer: true,
			observeParents: true,
			observeSlideChildren: true,

			// Prevent unnecessary slide resets
			preventInteractionOnTransition: true,

			// Improved loop behavior

			autoplay: {
			  delay: 5000,
			  disableOnInteraction: false,
			  pauseOnMouseEnter: true
			}
		});

		// Log successful initialization for debugging
		console.log('Reviews slider initialized successfully');
	}

	/**
	 * Ensure placeholder before nav to restore original position on wide screens
	 */
	ensureNavPlaceholder() {
		if (!this.navElement) return;
		if (this.root.querySelector('.reviews-slider__nav-placeholder')) {
			this.navPlaceholder = this.root.querySelector('.reviews-slider__nav-placeholder');
			return;
		}
		var span = document.createElement('span');
		span.className = 'reviews-slider__nav-placeholder';
		span.setAttribute('aria-hidden', 'true');
		this.navElement.parentNode.insertBefore(span, this.navElement);
		this.navPlaceholder = span;
	}

	/**
	 * Setup additional accessibility features
	 */
	setupAccessibility() {
		if (!this.swiperContainer) return;

		// Make slides focusable for keyboard navigation
		const slides = this.swiperContainer.querySelectorAll('.swiper-slide');
		slides.forEach(slide => {
			slide.setAttribute('tabindex', '0');
			slide.setAttribute('role', 'listitem');

			// Add keyboard event listeners for accessibility
			slide.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.swiper.slideTo(this.swiper.clickedIndex);
				}
			});
		});

		// Ensure container has proper role
		this.swiperContainer.setAttribute('role', 'region');
		this.swiperContainer.setAttribute('aria-label', 'Customer Reviews Carousel');
	}

	/**
	 * Move navigation elements based on screen width
	 * Moves .reviews-slider__nav into .reviews-slider__write-review when screen width <= 992px
	 * Restores nav to its original position (before placeholder) when > 992px
	 */
	moveNavigationElements() {
		const navElement = this.navElement;
		const writeReviewElement = this.writeReviewElement;
		const placeholder = this.navPlaceholder;

		if (!navElement || !writeReviewElement) return;

		if (window.innerWidth <= 992) {
			// Move navigation into write-review section within this section
			if (navElement.parentElement !== writeReviewElement) {
				writeReviewElement.appendChild(navElement);
			}
		} else if (placeholder && placeholder.parentNode) {
			// Move back to the original position before the placeholder
			if (navElement.previousSibling !== placeholder) {
				placeholder.parentNode.insertBefore(navElement, placeholder);
			}
		}

		// Update swiper to reflect the changes
		if (this.swiper && typeof this.swiper.update === 'function') {
			this.swiper.update();
		}
	}

	/**
	 * Setup resize handler for better responsiveness
	 */
	setupResizeHandler() {
		// Debounce function to limit resize event firing
		const debounce = (func, delay) => {
			let timeoutId;
			return function () {
				const context = this;
				const args = arguments;
				clearTimeout(timeoutId);
				timeoutId = setTimeout(() => {
					func.apply(context, args);
				}, delay);
			};
		};

		// Update swiper and move navigation elements on resize with debounce
		const handleResize = debounce(() => {
			this.moveNavigationElements();
		}, 200);

		// Add event listener
		window.addEventListener('resize', handleResize);

		// Call once on initialization to set correct position
		this.moveNavigationElements();
	}
}

// Initialize the slider when the DOM is fully loaded
function initAllReviewsSliders() {
	var sections = document.querySelectorAll('.reviews-slider');
	if (!sections || !sections.length) return;
	sections.forEach(function (root) { new ReviewsSlider(root); });
}

document.addEventListener('DOMContentLoaded', initAllReviewsSliders);
document.addEventListener('shopify:section:load', initAllReviewsSliders);